import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { siteEventApi } from '../api';
import { SiteEvent } from '../types';
import { toImageUrl } from '../utils/image';
import { WELCOME_EVENT } from '../data/events';
import WelcomeCouponEvent from '../components/events/WelcomeCouponEvent';

const EventViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [events, setEvents] = useState<SiteEvent[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    siteEventApi.getList()
      .then(r => setEvents(r.data.data))
      .catch(() => setEvents([]))
      .finally(() => setLoaded(true));
  }, []);

  const isWelcome = id === 'welcome-coupon';
  const current = events.find(e => String(e.id) === id);

  if (loaded && !isWelcome && !current) return <Navigate to="/events" replace />;

  const sidebar = [
    { id: WELCOME_EVENT.id, title: WELCOME_EVENT.title },
    ...events.map(e => ({ id: String(e.id), title: e.title })),
  ];

  const heading = isWelcome ? WELCOME_EVENT.title : (current?.title ?? '');
  const period = current && (current.startAt || current.endAt)
    ? `${current.startAt ? new Date(current.startAt).toLocaleDateString() : ''} ~ ${current.endAt ? new Date(current.endAt).toLocaleDateString() : ''}`
    : '';

  return (
    <div style={s.wrap}>
      <aside style={s.side}>
        <h3 style={s.sideTitle}>Events</h3>
        {sidebar.map(e => (
          <Link
            key={e.id}
            to={`/events/${e.id}`}
            style={{ ...s.sideItem, ...(e.id === id ? s.sideItemActive : {}) }}
          >
            {e.title}
          </Link>
        ))}
      </aside>
      <main style={s.main}>
        <h2 style={s.title}>{heading}</h2>
        {period && <div style={s.period}>{period}</div>}
        {isWelcome ? (
          <WelcomeCouponEvent />
        ) : current ? (
          <div style={s.detail}>
            <img src={toImageUrl(current.imageUrl)} alt={current.title} style={s.banner} />
            {current.description && <p style={s.desc}>{current.description}</p>}
          </div>
        ) : null}
      </main>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 1040, margin: '40px auto', padding: '0 20px', display: 'flex', gap: 32, alignItems: 'flex-start' },
  side: { width: 220, flexShrink: 0 },
  sideTitle: { fontSize: 16, fontWeight: 800, color: '#333', margin: '0 0 14px' },
  sideItem: { display: 'block', padding: '12px 14px', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#666', textDecoration: 'none', marginBottom: 6, background: '#f6f4fa' },
  sideItemActive: { background: '#b39ddb', color: '#fff' },
  main: { flex: 1, minWidth: 0 },
  title: { fontSize: 22, fontWeight: 800, color: '#333', margin: '0 0 4px', textAlign: 'center' },
  period: { fontSize: 13, color: '#999', margin: '0 0 24px', textAlign: 'center' },
  detail: { maxWidth: 640, margin: '0 auto', textAlign: 'center' },
  banner: { width: '100%', height: 'auto', display: 'block', borderRadius: 16 },
  desc: { fontSize: 15, color: '#555', lineHeight: 1.8, marginTop: 24, textAlign: 'left', whiteSpace: 'pre-wrap' },
};

export default EventViewPage;
